import { cn } from "@/lib/utils";

/**
 * The mark that says a feature is recent. Two forms, one meaning.
 *
 * ⚠️ **Deliberately quieter than the follow-up count beside it.** That badge is solid
 * `bg-primary` because it is an OBLIGATION — somebody promised a caller they would ring back
 * this morning. This is an ANNOUNCEMENT, and if the two shout equally then the one that matters
 * stops being noticed. So this is a tinted pill in the same hue rather than a filled one: the
 * hierarchy is the design, not the colour.
 *
 * ⚠️ Not red and not amber. Red is an SLA breach in this product and amber is
 * awaiting-the-requester (D-117); a navigation row borrowing either would report a problem
 * where there is only news.
 *
 * ⚠️ Built on the generic `primary` tokens rather than the sidebar's. The two resolve to
 * identical values in both themes today, and the generic ones mean this works unchanged the
 * first time something outside the rail — a tab, a settings row — needs announcing.
 */
export function NewBadge({
  variant = "pill",
  className,
}: {
  /**
   * `pill` carries the word and is read out as part of the row. `dot` is for the collapsed
   * rail, where there is no room for three letters.
   *
   * ⚠️ The dot is `aria-hidden` and the word travels on the row's own `aria-label` instead —
   * the same split the collapsed follow-up count already uses. A bare dot with an accessible
   * name of its own would be announced as a second, nameless thing beside the row.
   */
  variant?: "pill" | "dot";
  className?: string;
}) {
  if (variant === "dot") {
    return (
      <span
        aria-hidden="true"
        className={cn("h-2 w-2 shrink-0 rounded-full bg-primary", className)}
      />
    );
  }

  return (
    <span
      className={cn(
        "rounded-full bg-primary/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-primary ring-1 ring-inset ring-primary/25",
        className,
      )}
    >
      New
    </span>
  );
}
